import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const VALUES = [
  {
    title: "Speed",
    desc: "Rapid delivery without compromising quality. We move fast because we know the market waits for no one.",
    icon: "ri-flashlight-line",
  },
  {
    title: "Transparency",
    desc: "You always know where your project stands. Clear reporting, honest communication, no surprises.",
    icon: "ri-eye-line",
  },
  {
    title: "Efficiency",
    desc: "Measurable results at every stage. Data-driven decisions that maximize ROI and minimize waste.",
    icon: "ri-line-chart-line",
  },
];

const TEAM = [
  { name: "James Caldwell", role: "CEO & Founder" },
  { name: "Oliver Reeves", role: "CTO" },
  { name: "Liam Thornton", role: "Lead Developer" },
  { name: "Sophia Bennett", role: "UI/UX Design Lead" },
  { name: "Emma Whitfield", role: "Marketing Director" },
  { name: "Isla Harrington", role: "QA Lead" },
];

const STATS = [
  { value: "50+", label: "Apps shipped" },
  { value: "12M+", label: "Users reached" },
  { value: "30+", label: "Team members" },
  { value: "6+", label: "Years on the market" },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20interior%20with%20team%20collaboration%20space%2C%20warm%20ambient%20lighting%2C%20clean%20sophisticated%20design%2C%20professional%20business%20environment%2C%20warm%20amber%20and%20cream%20tones%2C%20editorial%20photography%20style%2C%20high%20detail%2C%20harmonious%20composition%2C%20no%20text&width=1600&height=900&seq=about-hero-01&orientation=landscape"
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink-950/70 via-ink-950/50 to-ink-950/30"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 mb-5">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-amber-200">
                  About Us
                </span>
              </div>
              <h1 className="font-display font-bold text-cream-50 leading-[1.08] tracking-tight text-4xl md:text-5xl lg:text-6xl">
                Trevica LTD
              </h1>
              <p className="mt-5 text-sm md:text-base text-cream-50/80 leading-relaxed max-w-lg">
                Where development meets marketing. We build utility applications and grow them into products millions rely on.
              </p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-2 order-2 lg:order-1">
                <div className="relative rounded-2xl overflow-hidden">
                  <div className="relative aspect-[4/5] w-full">
                    <img
                      src="https://storage.readdy-site.link/project_files/564b8566-d4d5-4635-bf3c-ab9b4b833508/58b73f03-e09f-4760-8446-bc1e47876562_compressed_ChatGPT-Image-13-.-2026-.-15_28_56.webp"
                      alt="Trevica team collaboration"
                      className="absolute inset-0 w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
              <div className="lg:col-span-3 order-1 lg:order-2">
                <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl">
                  Where development meets marketing
                </h2>
                <p className="mt-5 text-sm md:text-base text-ink-700 leading-relaxed">
                  We are an IT company specializing in the development of utility applications — from cleaners
                  and optimizers to AI services — and their full-cycle promotion and advertising support.
                </p>
                <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed">
                  Our expertise spans several key areas: mobile and desktop utility development, IT consulting,
                  and digital marketing services for applications. We help create tech-driven products and
                  effectively bring them to market — from ASO and media buying to scaling user bases.
                </p>
                <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed">
                  We accompany projects at all stages — from idea and analytics through development, launch,
                  promotion, and ongoing support. Thanks to our strong team of project managers, developers,
                  UI/UX designers, marketers, and QA engineers, we unite development and marketing into a
                  single application growth strategy.
                </p>
                <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed">
                  We also provide IT consultations, helping teams make informed technology decisions when
                  developing utility applications, optimize processes, and implement modern tools.
                </p>
                <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed">
                  A separate and key direction is advertising promotion of applications: we develop user
                  acquisition (UA) strategies, strengthen presence in stores, increase brand awareness, and
                  help applications find their audience among millions of competitors.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="relative w-full bg-cream-100 py-20 md:py-28 border-y border-ink-950/5">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-moss-500"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
                  Our Values
                </span>
              </div>
              <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl">
                What drives us every day
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {VALUES.map((v) => (
                <div
                  key={v.title}
                  className="group bg-cream-50 rounded-2xl border border-ink-950/5 p-8 md:p-10 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-100 mb-6 group-hover:bg-amber-200 transition-colors">
                    <i className={`${v.icon} text-xl text-amber-600`}></i>
                  </div>
                  <h3 className="font-display font-bold text-xl md:text-2xl text-ink-950 mb-3">
                    {v.title}
                  </h3>
                  <p className="text-sm md:text-base text-ink-700 leading-relaxed">
                    {v.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                <span className="text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-ink-700">
                  Our Team
                </span>
              </div>
              <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl">
                Meet the people behind Trevica
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-sm md:text-base text-ink-700 leading-relaxed">
                Experienced project managers, developers, designers, marketers, and QA engineers — united by a passion for building great products.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {TEAM.map((member) => (
                <div key={member.name} className="group bg-cream-100 rounded-2xl border border-ink-950/5 p-6 md:p-8 hover:-translate-y-1 transition-all duration-300 flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-amber-100 shrink-0">
                    <span className="font-display font-bold text-lg text-amber-600">
                      {member.name.split(" ").map((n) => n[0]).join("")}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg text-ink-950">{member.name}</h3>
                    <p className="text-xs uppercase tracking-widest text-ink-500 font-medium mt-1">{member.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="relative w-full bg-ink-950 py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              {STATS.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-display font-bold text-3xl md:text-5xl text-amber-400">
                    {stat.value}
                  </p>
                  <p className="mt-2 text-xs md:text-sm text-cream-50/60 uppercase tracking-widest">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full bg-cream-50 py-20 md:py-28">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14 text-center">
            <h2 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Ready to build something great?
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-sm md:text-base text-ink-700 leading-relaxed">
              Let&rsquo;s discuss your project. We&rsquo;ll analyze your needs and propose the best path forward — no strings attached.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-ink-950 px-7 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Get in touch
                <i className="ri-arrow-right-line text-base"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}